/**
 * Shapes the Feedback Lab and the focus group admin exchange with the API.
 *
 * Kept apart from `api.ts` because that file is already the whole school
 * product, and because the split makes one thing obvious at a glance: the
 * tester-facing shapes carry no `priority`, no `assignedTo` and no internal
 * notes. Those are absent on the server too — stripped before serialising —
 * and the types are written to agree rather than to paper over it.
 */

import type { Edition } from '@cornerstone/education-domain'

export type ProfessionalRole =
  | 'superintendent' | 'principal' | 'assistant_principal' | 'teacher' | 'counselor' | 'registrar'
  | 'attendance_clerk' | 'school_secretary' | 'nurse' | 'technology_director' | 'curriculum_director'
  | 'special_education' | 'parent' | 'student' | 'other'

export type Sentiment = 'love' | 'good' | 'okay' | 'needs_work' | 'hate'

export type FeedbackCategory =
  | 'love' | 'confusing' | 'too_many_clicks' | 'missing_something' | 'would_not_use' | 'saves_time'
  | 'feels_outdated' | 'hard_to_understand' | 'found_a_bug' | 'have_an_idea' | 'helps_my_school'
  | 'blocks_purchase' | 'drives_purchase' | 'other'

export type FeedbackStatus =
  | 'new' | 'reviewing' | 'planned' | 'in_progress' | 'completed' | 'declined' | 'needs_follow_up'

export type InviteStatus =
  | 'draft' | 'scheduled' | 'invited' | 'delivered' | 'opened' | 'accepted' | 'declined' | 'expired' | 'bounced'

export type AccessStatus = 'pending' | 'active' | 'completed' | 'disabled'

export interface JoinPreview {
  ok: boolean
  reason?: string
  message?: string
  firstName?: string
  lastName?: string
  email?: string
  organisation?: string
  professionalRole?: ProfessionalRole
  cohortName?: string | null
  cohortDescription?: string | null
  expiresAt?: string
  hasAccount?: boolean
}

export interface TesterSummary {
  total: number
  invited: number
  accepted: number
  active: number
  pending: number
  expired: number
  completed: number
  disabled: number
  bounced: number
}

export interface TesterRow {
  id: string
  displayName: string
  email: string
  professionalRole: ProfessionalRole
  jobTitle: string | null
  organisation: string
  cohortId: string | null
  inviteStatus: InviteStatus
  accessStatus: AccessStatus
  isPriorityTester: boolean
  participationScore: number
  feedbackCount: number
  bugCount: number
  modulesCompleted: number
  lastActiveAt: string | null
  joinedAt: string | null
  createdAt: string
}

export interface InvitationRow {
  id: string
  status: InviteStatus
  expiresAt: string
  sentAt: string | null
  openedAt: string | null
  acceptedAt: string | null
  revokedAt: string | null
  remindersSent: number
  history: { at: string; event: string; detail?: string; by?: string | null }[]
}

export interface TesterDetail {
  tester: TesterRow & {
    district?: string | null
    schoolType?: string | null
    schoolSize?: string | null
    yearsInEducation?: number | null
    currentSis?: string | null
    currentLms?: string | null
    capabilities: Record<string, boolean>
    rewards: { freeYearEligible: boolean; lifetimeDrawEligible: boolean; lifetimeWinner: boolean; notes?: string }
    internalNotes?: string
    agreement?: { version: string; acceptedAt: string } | null
    assignedModuleIds?: string[]
    pagesReviewed?: string[]
  }
  cohort: CohortRow | null
  eligibility: { freeYear: boolean; lifetimeDraw: boolean; shortfall: string[] }
  invitations: InvitationRow[]
}

export interface CohortRow {
  id: string
  name: string
  description?: string
  startDate?: string | null
  endDate?: string | null
  status: 'draft' | 'recruiting' | 'active' | 'complete' | 'archived'
  testerGoal?: number | null
  counts?: { total: number; invited: number; accepted: number; active: number; completed: number }
}

export interface EmailRow {
  id: string
  to: string
  subject: string
  template: string
  templateLabel: string
  status: 'queued' | 'sending' | 'sent' | 'failed' | 'cancelled'
  attempts: number
  queuedAt: string
  sentAt: string | null
  failureReason: string | null
  isTest: boolean
}

export interface EmailQueueState {
  sender: string
  senderMode: 'managed' | 'branded'
  queued: EmailRow[]
  failed: EmailRow[]
}

export interface FeedbackContext {
  route: string
  moduleId: string
  moduleName: string
  pageName: string
  professionalRole: ProfessionalRole
  edition: Edition
  viewport: { width: number; height: number }
  capturedAt: string
}

export interface TestingModuleRow {
  id: string
  key: string
  name: string
  blurb: string
  entryRoute: string
  estimatedMinutes: number
  pages: { key: string; name: string; route: string }[]
  feedbackCount: number
  pagesReviewed: number
  lastReviewedAt: string | null
}

export interface FeedbackLabState {
  tester: {
    id: string
    firstName: string
    displayName: string
    professionalRole: ProfessionalRole
    organisation: string
    capabilities: Record<string, boolean>
    participationScore: number
  }
  progress: {
    modulesTotal: number
    modulesReviewed: number
    pagesReviewed: number
    feedbackSubmitted: number
    unreadReplies: number
    percent: number
  }
  modules: TestingModuleRow[]
}

/** What a tester sees of their own submission. No triage, by construction. */
export interface MyFeedbackItem {
  id: string
  createdAt: string
  context: FeedbackContext
  sentiment: Sentiment | null
  categories: FeedbackCategory[]
  comment: string
  loves: string[]
  hates: string[]
  needs: string[]
  wants: string[]
  buyingSignal: string | null
  status: FeedbackStatus
  hasUnreadReply: boolean
  replies: { id: string; at: string; from: string; note: string }[]
}

export interface IdeaRow {
  id: string
  title: string
  description: string
  category: 'must_have' | 'should_have' | 'nice_to_have' | 'future'
  moduleId: string | null
  voteCount: number
  votesByRole: Partial<Record<ProfessionalRole, number>>
  status: FeedbackStatus
  hasVoted: boolean
  priority?: string
}

export interface MissionStepRow {
  order: number
  instruction: string
  route?: string | null
}

export interface MissionRow {
  id: string
  title: string
  scenario: string
  steps: MissionStepRow[]
  estimatedMinutes: number
  completed: boolean
  openAttemptId: string | null
}

export interface EarnedBadge {
  key: string
  label: string
  description: string
  awardedAt?: string
}

export interface PollOptionRow {
  key: string
  label: string
  detail?: string
}

export interface PollRow {
  id: string
  question: string
  options: PollOptionRow[]
  answered: boolean
  results: { key: string; count: number; percent: number }[] | null
}

export interface StaffFeedbackItem {
  id: string
  testerId: string
  screenshotIds?: string[]
  testerName: string
  professionalRole: ProfessionalRole
  organisation: string
  createdAt: string
  context: FeedbackContext
  sentiment: Sentiment | null
  sentimentScore: number | null
  categories: FeedbackCategory[]
  comment?: string
  loves?: string[]
  hates?: string[]
  needs?: string[]
  wants?: string[]
  buyingSignal?: string | null
  status: FeedbackStatus
  priority: 'critical' | 'high' | 'medium' | 'low' | 'none'
  notes?: { id: string; at: string; by: string; byName: string; note: string; visibility: 'internal' | 'tester' }[]
}

export interface FeedbackInboxState {
  summary: {
    total: number
    new: number
    needsFollowUp: number
    averageSentiment: number | null
    blocksPurchase: number
    drivesPurchase: number
  }
  byModule: { moduleId: string; moduleName: string; count: number; averageSentiment: number }[]
  items: StaffFeedbackItem[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Labels, kept beside the types they describe
// ─────────────────────────────────────────────────────────────────────────────

export const SENTIMENTS: { key: Sentiment; emoji: string; label: string }[] = [
  { key: 'love', emoji: '😍', label: 'Love it' },
  { key: 'good', emoji: '🙂', label: 'Pretty good' },
  { key: 'okay', emoji: '😐', label: "It's okay" },
  { key: 'needs_work', emoji: '😕', label: 'Needs work' },
  { key: 'hate', emoji: '😤', label: 'I hate it' },
]

export const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  love: 'I love this',
  confusing: 'This is confusing',
  too_many_clicks: 'Takes too many clicks',
  missing_something: 'Something is missing',
  would_not_use: "I wouldn't use this",
  saves_time: 'This would save me time',
  feels_outdated: 'This feels outdated',
  hard_to_understand: 'Hard to understand',
  found_a_bug: 'I found a bug',
  have_an_idea: 'I have an idea',
  helps_my_school: 'This would help my school',
  blocks_purchase: 'This would stop me buying',
  drives_purchase: 'This would make me buy',
  other: 'Something else',
}

export const LOVE_SUGGESTIONS = [
  'Easy to use', 'Looks good', 'Saves time', 'Makes sense',
  'Better than our current system', 'Fast', 'Useful information', 'Good workflow',
]

export const HATE_SUGGESTIONS = [
  'Too many clicks', 'Confusing', 'Hard to find', 'Too much information',
  'Not enough information', 'Slow', 'Feels unnecessary', 'Poor workflow', 'Missing a feature',
]

export const PROFESSIONAL_ROLE_LABELS: Record<ProfessionalRole, string> = {
  superintendent: 'Superintendent',
  principal: 'Principal',
  assistant_principal: 'Assistant Principal',
  teacher: 'Teacher',
  counselor: 'Counselor',
  registrar: 'Registrar',
  attendance_clerk: 'Attendance Clerk',
  school_secretary: 'School Secretary',
  nurse: 'Nurse',
  technology_director: 'Technology Director',
  curriculum_director: 'Curriculum Director',
  special_education: 'Special Education Staff',
  parent: 'Parent',
  student: 'Student',
  other: 'Other',
}

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  planned: 'Planned',
  in_progress: 'In progress',
  completed: 'Completed',
  declined: 'Declined',
  needs_follow_up: 'Needs follow-up',
}

export const INVITE_STATUS_LABELS: Record<InviteStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  invited: 'Invited',
  delivered: 'Delivered',
  opened: 'Opened',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  bounced: 'Bounced',
}

/**
 * Which route belongs to which testing module.
 *
 * Longest prefix wins, so `/students/success` resolves to Student Success
 * rather than to Students. This is what makes feedback attach itself without
 * asking the tester where they are — the brief's "do not make testers
 * repeatedly explain where they are in the system".
 */
const ROUTE_MAP: { prefix: string; moduleId: string; moduleName: string; pageName: string }[] = [
  { prefix: '/students/success', moduleId: 'module-success', moduleName: 'Student Success', pageName: 'Success centre' },
  { prefix: '/students/', moduleId: 'module-students', moduleName: 'Students', pageName: 'Student profile' },
  { prefix: '/students', moduleId: 'module-students', moduleName: 'Students', pageName: 'Student list' },
  { prefix: '/attendance/section', moduleId: 'module-attendance', moduleName: 'Attendance', pageName: 'Class register' },
  { prefix: '/attendance', moduleId: 'module-attendance', moduleName: 'Attendance', pageName: 'Daily attendance' },
  { prefix: '/grades/', moduleId: 'module-gradebook', moduleName: 'Gradebook', pageName: 'Gradebook' },
  { prefix: '/grades', moduleId: 'module-gradebook', moduleName: 'Gradebook', pageName: 'Class list' },
  { prefix: '/academics', moduleId: 'module-academics', moduleName: 'Classes & Scheduling', pageName: 'Sections' },
  { prefix: '/scheduling', moduleId: 'module-academics', moduleName: 'Classes & Scheduling', pageName: 'Scheduling' },
  { prefix: '/reports', moduleId: 'module-reports', moduleName: 'Reports & Documents', pageName: 'Report centre' },
  { prefix: '/administration', moduleId: 'module-administration', moduleName: 'Administration', pageName: 'Administration' },
  { prefix: '/operations', moduleId: 'module-dashboard', moduleName: 'Dashboard', pageName: 'Operations' },
  { prefix: '/classes', moduleId: 'module-family', moduleName: 'Parent & Student Portal', pageName: 'Classes' },
  { prefix: '/assignments', moduleId: 'module-family', moduleName: 'Parent & Student Portal', pageName: 'Assignments' },
]

export function describeRoute(pathname: string, portal: 'staff' | 'family') {
  const match = ROUTE_MAP.find((entry) => pathname === entry.prefix || pathname.startsWith(entry.prefix))
  if (match) return match
  if (pathname === '/') {
    return portal === 'family'
      ? { moduleId: 'module-family', moduleName: 'Parent & Student Portal', pageName: 'Family dashboard' }
      : { moduleId: 'module-dashboard', moduleName: 'Dashboard', pageName: 'Staff dashboard' }
  }
  return { moduleId: 'module-other', moduleName: 'Other', pageName: pathname }
}
